import React from 'react';
import {
  IBlockData,
  BasicType,
  AdvancedType,
  createCustomBlock,
  RecursivePartial,
} from '../../..';
import { BasicBlock } from '../../../components/BasicBlock';
import { Section, Column } from '../../../components';
import { t } from '../../../utils/I18nManager';
import { resolvePreviewColor } from '@core/utils';
import { merge } from 'lodash';
import * as Handlebars from 'handlebars';
import { singleUserLayouts } from '../../../data';
import { getParentByIdx } from '../../../utils/block';

interface SingleUserItem {
  id: string;
  name?: string;
  address?: string;
  licenceNumber?: string;
  phone?: string;
  profileImage?: string;
  title?: string; // Realtor or Broker
  email?: string;
}

interface ISingleUserData {
  selectedUserId?: string;
  layout?: 'stacked' | 'card';
}

interface ISingleUserAttributes extends Record<string, any> {
  'background-color': string;
  'user-name-color': string;
  'user-title-color': string;
  'user-address-color': string;
  'user-phone-color': string;
  'user-licence-color': string;
  'title-font-size': string;
  'name-font-size': string;
  'address-font-size': string;
  'button-color': string;
  'button-text-color': string;
  'button-font-size': string;
  'button-text': string;
  'show-name': boolean;
  'show-title': boolean;
  'show-address': boolean;
  'show-phone': boolean;
  'show-licence': boolean;
  padding: string;
  title: string;
}

export type ISingleUser = IBlockData<ISingleUserAttributes, ISingleUserData>;

const CustomBlockType = {
  SINGLE_USER_BLOCK: 'text_single_user' as const,
};

export const SingleUserBlock = createCustomBlock<ISingleUser>({
  name: 'Single',
  type: CustomBlockType.SINGLE_USER_BLOCK,
  validParentType: [
    // Allow broader parent types like advanced content blocks
    BasicType.PAGE,
    BasicType.WRAPPER,
    BasicType.SECTION,
    BasicType.GROUP,
    BasicType.COLUMN,
    BasicType.HERO,
    AdvancedType.WRAPPER,
    AdvancedType.SECTION,
    AdvancedType.GROUP,
    AdvancedType.COLUMN,
    AdvancedType.HERO,
  ],
  create: (payload?: RecursivePartial<ISingleUser>) => {
    const defaultData: ISingleUser = {
      type: CustomBlockType.SINGLE_USER_BLOCK,
      data: {
        value: {
          selectedUserId: '1',
          layout: 'stacked',
        },
      },
      attributes: {
        'background-color': '#ffffff',
        'user-name-color': '#000000',
        'user-title-color': '#0066cc',
        'user-address-color': '#666666',
        'user-phone-color': '#666666',
        'user-licence-color': '#666666',
        'title-font-size': '16px',
        'name-font-size': '18px',
        'address-font-size': '14px',
        'button-color': '#007bff',
        'button-text-color': '#ffffff',
        'button-font-size': '14px',
        'button-text': 'Contact Agent',
        'show-name': true,
        'show-title': true,
        'show-address': true,
        'show-phone': true,
        'show-licence': true,
        padding: '20px',
        title: 'Single',
      },
      children: [],
    };
    return merge(defaultData, payload);
  },
  render: (params: {
    data: ISingleUser;
    idx?: string | null;
    mode: 'testing' | 'production';
    context?: any;
    dataSource?: { [key: string]: any };
    children?: React.ReactNode;
    keepClassName?: boolean;
    renderPortal?: any;
  }) => {
    const { data, idx } = params;
    const selectedUserId = data.data.value.selectedUserId || '1';
    const selectedLayout = data.data.value.layout || 'stacked';

    // Mock users data
    const mockUsers: SingleUserItem[] = [
      {
        id: '1',
        name: 'John Smith',
        address: '123 Real Estate Blvd, Suite 100, Anytown, CA 90210',
        licenceNumber: 'DRE# 12345678',
        phone: '(555) 123-4567',
        title: 'Senior Realtor',
        email: 'john.smith@realestate.com',
        profileImage:
          'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face',
      },
      {
        id: '2',
        name: 'Sarah Johnson',
        address: '456 Property Lane, Suite 200, Los Angeles, CA 90024',
        licenceNumber: 'DRE# 87654321',
        phone: '(555) 234-5678',
        title: 'Broker Associate',
        email: 'sarah.johnson@realestate.com',
        profileImage:
          'https://images.unsplash.com/photo-1494790108755-2616b9b01d1a?w=200&h=200&fit=crop&crop=face',
      },
      {
        id: '3',
        name: 'Michael Chen',
        address: '789 Realty Ave, Floor 3, Chicago, IL 60611',
        licenceNumber: 'LIC# 11223344',
        phone: '(555) 345-6789',
        title: 'Commercial Broker',
        email: 'michael.chen@realestate.com',
        profileImage:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face',
      },
      {
        id: '4',
        name: 'Emily Davis',
        address: '321 Home Street, Suite 50, Miami, FL 33101',
        licenceNumber: 'FL# 55667788',
        phone: '(555) 456-7890',
        title: 'Luxury Specialist',
        email: 'emily.davis@realestate.com',
        profileImage:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face',
      },
      {
        id: '5',
        name: 'Robert Wilson',
        address: '567 Estate Blvd, Suite 75, Seattle, WA 98101',
        licenceNumber: 'WA# 99887766',
        phone: '(555) 567-8901',
        title: 'Investment Specialist',
        email: 'robert.wilson@realestate.com',
        profileImage:
          'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=face',
      },
      {
        id: '6',
        name: 'Lisa Anderson',
        address: '890 Market Plaza, Floor 12, Austin, TX 73301',
        licenceNumber: 'TX# 44556677',
        phone: '(555) 678-9012',
        title: 'First-Time Buyer Specialist',
        email: 'lisa.anderson@realestate.com',
        profileImage:
          'https://images.unsplash.com/photo-1554151228-14d9def656e4?w=200&h=200&fit=crop&crop=face',
      },
    ];

    // Find the selected user
    const selectedUser = mockUsers.find(user => user.id === selectedUserId);

    // Use first user as fallback
    const displayUser = selectedUser || mockUsers[0];

    // Find the layout configuration
    const layoutConfig = singleUserLayouts.find(
      (layout: any) => layout.id === selectedLayout,
    );
    if (!layoutConfig) {
      console.error(`Layout "${selectedLayout}" not found`);
      return (
        <BasicBlock
          params={params}
          tag='mj-raw'
        >
          <div>Layout configuration not found</div>
        </BasicBlock>
      );
    }

    // Prepare handlebars data (single user)
    const name = displayUser.name || 'Agent Name';
    const title = displayUser.title || 'Realtor';
    const address = displayUser.address || 'Address not available';
    const phone = displayUser.phone || 'Phone not available';
    const licenceNumber = displayUser.licenceNumber || 'License not available';
    const email = displayUser.email || 'email@example.com';
    const profileImage =
      displayUser.profileImage ||
      'https://placehold.co/200x200/e1e1e1/666666?text=Profile';

    // Generate required CSS classes for block selection
    const isTest = params.mode === 'testing';
    let cssClasses = 'email-block';
    if (isTest && idx) {
      cssClasses = `email-block node-idx-${idx} node-type-${data.type}`;
    }

    // Resolve dynamic brand-colour merge tags to real hex in preview/testing mode;
    // in production the raw tag is returned unchanged for send-time resolution.
    const resolveColor = (key: string, fallback: string) =>
      resolvePreviewColor(data.attributes[key], params.mode, params.dataSource) ||
      fallback;

    // Prepare template context
    const templateContext = {
      // Essential CSS classes for block selection
      cssClasses,
      blockTitle: data.attributes.title || 'Single',
      // Block attributes mapped to handlebars context
      backgroundColor: resolveColor('background-color', '#ffffff'),
      userNameColor: resolveColor('user-name-color', '#000000'),
      userTitleColor: resolveColor('user-title-color', '#0066cc'),
      userAddressColor: resolveColor('user-address-color', '#666666'),
      userPhoneColor: resolveColor('user-phone-color', '#666666'),
      userLicenceColor: resolveColor('user-licence-color', '#666666'),
      titleFontSize: data.attributes['title-font-size'] || '16px',
      buttonColor: resolveColor('button-color', '#007bff'),
      buttonTextColor: resolveColor('button-text-color', '#ffffff'),
      buttonText: data.attributes['button-text'] || 'Contact Agent',
      padding: data.attributes['padding'] || '20px',

      // Show/hide flags
      showName: data.attributes['show-name'] !== false,
      showTitle: data.attributes['show-title'] !== false,
      showAddress: data.attributes['show-address'] !== false,
      showPhone: data.attributes['show-phone'] !== false,
      showLicence: data.attributes['show-licence'] !== false,

      // Single user data
      name,
      title,
      address,
      phone,
      licenceNumber,
      email,
      profileImage,
    };

    try {
      // Compile and render the handlebars template
      const template = Handlebars.compile(layoutConfig.data);
      const htmlContent = template(templateContext);

      const contentNode = (
        <BasicBlock
          params={params}
          tag='mj-raw'
        >
          {htmlContent}
        </BasicBlock>
      );

      const parentBlockData = params.idx && params.context
        ? getParentByIdx({ content: params.context }, params.idx)
        : null;

      if (parentBlockData) {
        // If dropped at PAGE/WRAPPER, auto-wrap into Section -> Column
        if (
          parentBlockData.type === BasicType.PAGE ||
          parentBlockData.type === BasicType.WRAPPER ||
          parentBlockData.type === AdvancedType.WRAPPER
        ) {
          return (
            <Section padding='0px' text-align='left'>
              <Column>{contentNode}</Column>
            </Section>
          );
        }

        // If dropped at SECTION/GROUP, wrap into a single Column
        if (
          parentBlockData.type === BasicType.SECTION ||
          parentBlockData.type === AdvancedType.SECTION ||
          parentBlockData.type === BasicType.GROUP ||
          parentBlockData.type === AdvancedType.GROUP
        ) {
          return <Column>{contentNode}</Column>;
        }
      }

      // Default: already in COLUMN/HERO or parent unknown
      return contentNode;
    } catch (error) {
      console.error('Error rendering handlebars template:', error);
      console.error('Layout config:', layoutConfig);
      console.error('Template context:', templateContext);
      return (
        <BasicBlock
          params={params}
          tag='mj-raw'
        >
          Error rendering template: {(error as Error).message}
        </BasicBlock>
      );
    }
  },
});
